import * as React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";

/* helpers */
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const midpoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

export function ZoomPan({
  children,
  minScale = 0.5,
  maxScale = 3,
  initialScale = 1,
  initialPos = { x: 0, y: 0 },
  sx,
}) {
  const containerRef = React.useRef(null);

  const [scale, setScale] = React.useState(initialScale);
  const [pos, setPos] = React.useState(initialPos);

  // ✅ navigationEnabled=true => navegación habilitada (PAN/ZOOM) => 🔓
  const [navigationEnabled, setNavigationEnabled] = React.useState(false);

  const pointersRef = React.useRef(new Map());
  const gestureRef = React.useRef({
    mode: "none",
    startScale: 1,
    startPos: { x: 0, y: 0 },
    startDistance: 0,
    pinchWorld: { x: 0, y: 0 },
    panStart: { x: 0, y: 0 },
  });

  // para gatillar animación “pulse” en la botonera
  const [pulse, setPulse] = React.useState(0);

  const setTransform = (s, p) => {
    setScale(s);
    setPos(p);
  };

  const zoomAt = (nextScale, clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const wx = (x - pos.x) / scale;
    const wy = (y - pos.y) / scale;

    const s = clamp(nextScale, minScale, maxScale);
    setTransform(s, { x: x - wx * s, y: y - wy * s });
  };

  // Si se deshabilita navegación, limpiamos punteros/gesto para evitar estados colgados
  React.useEffect(() => {
    if (!navigationEnabled) {
      pointersRef.current.clear();
      gestureRef.current.mode = "none";
    }
  }, [navigationEnabled]);

  /* pointer events (solo cuando navigationEnabled=true) */
  const onPointerDown = (e) => {
    if (!navigationEnabled) return;

    containerRef.current.setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointersRef.current.size === 1) {
      gestureRef.current.mode = "pan";
      gestureRef.current.startPos = pos;
      gestureRef.current.panStart = { x: e.clientX, y: e.clientY };
    } else if (pointersRef.current.size === 2) {
      const [a, b] = pointersRef.current.values();
      const mid = midpoint(a, b);
      const d = dist(a, b);

      const rect = containerRef.current.getBoundingClientRect();
      const cx = mid.x - rect.left;
      const cy = mid.y - rect.top;

      gestureRef.current.mode = "pinch";
      gestureRef.current.startScale = scale;
      gestureRef.current.startDistance = d;
      gestureRef.current.pinchWorld = {
        x: (cx - pos.x) / scale,
        y: (cy - pos.y) / scale,
      };
    }
  };

  const onPointerMove = (e) => {
    if (!navigationEnabled) return;
    if (!pointersRef.current.has(e.pointerId)) return;

    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (gestureRef.current.mode === "pan") {
      const dx = e.clientX - gestureRef.current.panStart.x;
      const dy = e.clientY - gestureRef.current.panStart.y;
      setPos({
        x: gestureRef.current.startPos.x + dx,
        y: gestureRef.current.startPos.y + dy,
      });
    }

    if (gestureRef.current.mode === "pinch" && pointersRef.current.size >= 2) {
      const [a, b] = pointersRef.current.values();
      const d = dist(a, b);
      const mid = midpoint(a, b);

      const rect = containerRef.current.getBoundingClientRect();
      const cx = mid.x - rect.left;
      const cy = mid.y - rect.top;

      const raw = gestureRef.current.startScale * (d / gestureRef.current.startDistance);
      const s = clamp(raw, minScale, maxScale);

      setTransform(s, {
        x: cx - gestureRef.current.pinchWorld.x * s,
        y: cy - gestureRef.current.pinchWorld.y * s,
      });
    }
  };

  const onPointerUp = (e) => {
    pointersRef.current.delete(e.pointerId);
    gestureRef.current.mode = "none";
  };

  /* buttons */
  const zoomIn = () => {
    const r = containerRef.current.getBoundingClientRect();
    zoomAt(scale * 1.15, r.left + r.width / 2, r.top + r.height / 2);
  };

  const zoomOut = () => {
    const r = containerRef.current.getBoundingClientRect();
    zoomAt(scale * 0.85, r.left + r.width / 2, r.top + r.height / 2);
  };

  const reset = () => setTransform(initialScale, initialPos);

  const toggleMode = () => {
    setNavigationEnabled((v) => !v);
    setPulse((p) => p + 1);
  };

  // ✅ Labels coherentes con la semántica
  const modeLabel = navigationEnabled ? "Modo navegación (pan/zoom)" : "Modo selección";

  return (
    <Box
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      sx={{
        position: "relative",
        overflow: "hidden",
        touchAction: navigationEnabled ? "none" : "auto",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        ...sx,
      }}
    >
      {/* BOTONERA UNIFICADA + animación */}
      <Box
        key={pulse}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: navigationEnabled ? "primary.main" : "divider",
          borderRadius: 2,
          p: 0.5,
          boxShadow: 2,
          animation: "zpPulse 260ms ease-out",
          "@keyframes zpPulse": {
            "0%": { transform: "scale(1)", boxShadow: "0 0 0 rgba(0,0,0,0)" },
            "50%": { transform: "scale(1.03)", boxShadow: "0 0 0 6px rgba(25,118,210,0.12)" },
            "100%": { transform: "scale(1)", boxShadow: "0 0 0 rgba(0,0,0,0)" },
          },
        }}
      >
        <Tooltip title={modeLabel}>
          <IconButton size="small" onClick={toggleMode} aria-label={modeLabel}>
            <Box
              key={navigationEnabled ? "nav-on" : "nav-off"}
              sx={{
                display: "grid",
                placeItems: "center",
                animation: "zpIcon 180ms ease-out",
                "@keyframes zpIcon": {
                  "0%": { opacity: 0, transform: "rotate(-18deg) scale(0.9)" },
                  "100%": { opacity: 1, transform: "rotate(0deg) scale(1)" },
                },
              }}
            >
              {/* ✅ Icono correcto: navegación habilitada => 🔓 */}
              {navigationEnabled ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />}
            </Box>
          </IconButton>
        </Tooltip>

        <IconButton size="small" onClick={zoomIn} disabled={!navigationEnabled}>
          <AddIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={zoomOut} disabled={!navigationEnabled}>
          <RemoveIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={reset} disabled={!navigationEnabled}>
          <RestartAltIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* CONTENT */}
      <Box
        sx={{
          transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
          transformOrigin: "0 0",
          width: "fit-content",
        }}
      >
        {typeof children === "function" ? children({ navigationEnabled }) : children}
      </Box>
    </Box>
  );
}
